import { useState, useEffect, useMemo, useRef } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  useApproveRejectActionMutation,
  useLazyGetApprovalsByEntityQuery,
  useGetApprovalsCompletedByEntityQuery,
} from '@/store/query/approval-builder';
import { getAtsJobCreate, atsJobCreateApiActions } from '@/store/atsJobCreate';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useShowToast } from '@/hooks';

export type ApprovalActionStatus = 'approved' | 'rejected' | null;

interface UseApprovalProps {
  entityId?: number | string | null;
  entityIds?: number[] | string[] | null;
  type: 'requisition' | 'job' | 'offer';
  entityName?: string;
  onSuccess?: () => void;
}

interface UseApprovalReturn {
  actionStatus: ApprovalActionStatus;
  isApproveActionLoading: boolean;
  rejectModalActions: {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    onToggle: () => void;
    isControlled: boolean;
    getButtonProps: (props?: any) => any;
    getDisclosureProps: (props?: any) => any;
  };
  rejectApproveFields: Array<{
    name: string;
    type: string;
    label: string;
    placeholder: string;
  }>;
  handleApprove: () => void;
  handleReject: () => void;
  handleRejectSubmit: (values?: { note?: string }) => void;
}

export const useApproval = ({
  entityId,
  entityIds,
  type,
  entityName,
  onSuccess,
}: UseApprovalProps): UseApprovalReturn => {
  const [actionStatus, setActionStatus] = useState<ApprovalActionStatus>(null);
  const rejectModalActions = useDisclosure();
  const toast = useShowToast();

  const [approverAcceptAction, { isLoading: isApproveActionLoading }] =
    useApproveRejectActionMutation();

  // Determine entity name for messages
  let defaultEntityName: string;
  switch (type) {
    case 'requisition':
      defaultEntityName = 'Requisition';
      break;
    case 'job':
      defaultEntityName = 'Job';
      break;
    case 'offer':
      defaultEntityName = 'Offer';
      break;
    default:
      defaultEntityName = 'Entity';
      break;
  }
  const displayName = entityName || defaultEntityName;

  // Reject form configuration
  const rejectApproveFields = [
    {
      name: 'note',
      type: 'textArea',
      label: 'Notes',
      placeholder: 'Write your reason here',
    },
  ];

  const handleApprove = () => {
    if (!entityId && !entityIds?.[0]) return;

    approverAcceptAction({
      payload: {
        type,
        entity_id: entityId || entityIds?.[0],
        log_action: 'approve',
        note: '',
        ...(entityIds ? { apply_to_group: true } : {}),
      },
    }).then((response) => {
      if ('error' in response) {
        const message = response?.error?.[0];
        toast({
          title: `Unable to approve the ${displayName.toLowerCase()}`,
          description: message,
          status: 'error',
        });
      } else {
        toast({
          title: `${displayName} approved successfully`,
        });
        setActionStatus('approved');
        onSuccess?.();
      }
    });
  };

  const handleReject = () => {
    rejectModalActions.onOpen();
  };

  const handleRejectSubmit = (values: { note?: string } = {}) => {
    if (!entityId && !entityIds?.[0]) return;

    approverAcceptAction({
      payload: {
        type,
        entity_id: entityId || entityIds?.[0],
        log_action: 'reject',
        note: values.note || '--',
        ...(entityIds ? { apply_to_group: true } : {}),
      },
    }).then((response) => {
      if ('error' in response) {
        const message = response?.error?.[0];
        toast({
          title: `Unable to reject the ${displayName.toLowerCase()}`,
          description: message,
          status: 'error',
        });
      } else {
        toast({
          title: `${displayName} has been rejected`,
        });
        setActionStatus('rejected');
        rejectModalActions.onClose();
        onSuccess?.();
      }
    });
  };

  return {
    actionStatus,
    isApproveActionLoading,
    rejectModalActions,
    rejectApproveFields,
    handleApprove,
    handleReject,
    handleRejectSubmit,
  };
};

// Job-specific approval hook with URL action handling, approval data, etc.
export type JobApprovalStatus = 'approved' | 'rejected' | null;

interface UseJobApprovalProps {
  jobId: string | number | null | undefined;
  isStandaloneApprovalView?: boolean;
  isMobile?: boolean;
  onApproveSuccess?: () => void;
  onRejectSuccess?: () => void;
}

interface UseJobApprovalReturn {
  currentStatus: JobApprovalStatus | string;
  approvalStatus: string | undefined;
  approvalData: unknown;
  currentUserApprovalAction: JobApprovalStatus;
  isCurrentUserApprover: boolean;
  rejectActions: ReturnType<typeof useDisclosure>;
  rejectApproveFields: Array<{
    name: string;
    type: string;
    label: string;
    placeholder: string;
  }>;
  handleApproveForPublishing: () => void;
  handleRejectApprove: (values?: { note?: string }) => void;
  approveJob: () => Promise<void>;
  isApproverActionLoading: boolean;
  showStandaloneFooter: boolean;
  currentApproval: unknown;
  job_approval_enabled: boolean;
}

export const useJobApproval = ({
  jobId,
  isStandaloneApprovalView = false,
  isMobile = false,
  onApproveSuccess,
  onRejectSuccess,
}: UseJobApprovalProps): UseJobApprovalReturn => {
  const dispatch = useDispatch();
  const toast = useShowToast();
  const location = useLocation();
  const currUser = useSelector(selectCurrUser);
  const { currentJob = {} } = useSelector(getAtsJobCreate);
  const { data: jobDataFromStore = {} } = currentJob;
  const approvalStatus =
    jobDataFromStore?.approval_status ?? jobDataFromStore?.status;
  const { data: { status, job_approval_enabled } = {} } = currentJob;

  const [trigger, { data: currentApproval }] =
    useLazyGetApprovalsByEntityQuery();
  const { data: completedApprovalData } = useGetApprovalsCompletedByEntityQuery(
    {
      id: String(jobId),
      type: 'job',
    },
    {
      skip:
        !jobId ||
        !jobDataFromStore ||
        (approvalStatus !== 'approved' && approvalStatus !== 'rejected'),
    }
  );

  const approvalData =
    approvalStatus === 'pending_approval'
      ? currentApproval
      : completedApprovalData;

  const currentUserApprovalAction = useMemo((): JobApprovalStatus => {
    if (!approvalData?.approval_steps || !currUser?.id) return null;
    const currUserId = Number(currUser.id);
    for (const step of approvalData.approval_steps) {
      const groups = step?.approval_user_groups ?? [];
      for (const group of groups) {
        const users = group?.approval_users ?? [];
        for (const approvalUser of users) {
          const userId = approvalUser?.user?.id;
          if (userId == null) continue;
          if (Number(userId) === currUserId && approvalUser?.approval_log) {
            const action = approvalUser.approval_log?.action;
            if (action === 'approve') return 'approved';
            if (action === 'reject') return 'rejected';
            return null;
          }
        }
      }
    }
    return null;
  }, [approvalData, currUser?.id]);

  const currentStatus = currentUserApprovalAction || approvalStatus;

  const urlActionProcessedRef = useRef(false);
  const lastProcessedActionRef = useRef<string | null>(null);
  const urlActionTriggeredRef = useRef(false);
  const urlActionStatus = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('action');
  }, [location.search]);

  const currentActiveStep = currentApproval?.approval_steps?.find(
    (step: { status?: string }) => step?.status === 'in_progress'
  );
  const currentStepUsers =
    currentActiveStep?.approval_user_groups?.[0]?.approval_users
      ?.filter((user: { approval_log: unknown }) => user?.approval_log === null)
      ?.map((userObj: { user?: { id?: number } }) => userObj?.user?.id) ?? [];
  const currUserId = currUser?.id;
  const isCurrentUserApprover = currentStepUsers?.includes(currUserId);

  const rejectActions = useDisclosure();
  const [approverAction, { isLoading: isApproverActionLoading }] =
    useApproveRejectActionMutation();

  const rejectApproveFields = [
    {
      name: 'note',
      type: 'textArea',
      label: 'Notes',
      placeholder: 'Write your reason here',
    },
  ];

  const handleApproveForPublishing = () => {
    approverAction({
      payload: {
        type: 'job',
        entity_id: jobId,
        log_action: 'approve',
        note: '',
      },
    }).then((response: { error?: unknown }) => {
      if ('error' in response) {
        toast({
          title: 'Unable to approve the job',
          status: 'error',
        });
        return;
      }
      toast({
        title: 'Job approved successfully',
      });
      rejectActions.onClose();
      onApproveSuccess?.();
    });
  };

  const handleRejectApprove = (values: { note?: string } = {}) => {
    const wasUrlTriggered = urlActionTriggeredRef.current;
    approverAction({
      payload: {
        type: 'job',
        entity_id: jobId,
        log_action: 'reject',
        ...values,
      },
    }).then((response: { error?: unknown }) => {
      if ('error' in response) {
        const message = (response?.error as string[])?.[0];
        toast({
          title: 'Unable to reject the job',
          description: message,
          status: 'error',
        });
      } else {
        toast({
          title: 'Job has been rejected',
        });
        rejectActions.onClose();
        urlActionTriggeredRef.current = false;

        if (isStandaloneApprovalView && wasUrlTriggered) {
          dispatch(
            atsJobCreateApiActions('getCurrentJob', {
              pathParams: { jobId },
              isBackground: true,
            })
          );
          return;
        }

        onRejectSuccess?.();
      }
    });
  };

  const approveJob = async () => {
    const wasUrlTriggered = urlActionTriggeredRef.current;
    try {
      const response = await approverAction({
        payload: {
          type: 'job',
          entity_id: jobId,
          log_action: 'approve',
          note: '',
        },
      });

      if ('error' in response) {
        toast({
          title: 'Unable to approve the job',
          status: 'error',
        });
        return;
      }
      const updated = await trigger({ id: jobId, type: 'job' }, true);
      if (updated?.error) {
        toast({
          title: 'Failed to refresh job status',
          status: 'error',
        });
        return;
      }

      if (isStandaloneApprovalView && wasUrlTriggered) {
        urlActionTriggeredRef.current = false;
        toast({
          title: 'Job approved successfully',
          status: 'success',
        });
        dispatch(
          atsJobCreateApiActions('getCurrentJob', {
            pathParams: { jobId },
            isBackground: true,
          })
        );
        return;
      }

      toast({
        title: 'Job approved successfully',
        status: 'success',
      });
      onApproveSuccess?.();
    } catch {
      toast({
        title: 'Something went wrong',
        status: 'error',
      });
    }
  };

  useEffect(() => {
    if (status === 'pending_approval') {
      trigger({ id: jobId, type: 'job' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, status]);

  useEffect(() => {
    if (!isStandaloneApprovalView || !urlActionStatus) return;
    if (
      urlActionProcessedRef.current &&
      lastProcessedActionRef.current === urlActionStatus
    ) {
      return;
    }
    if (!jobId || !jobDataFromStore) return;

    if (approvalStatus === 'approved' || approvalStatus === 'rejected') {
      urlActionProcessedRef.current = true;
      lastProcessedActionRef.current = urlActionStatus;
      return;
    }
    if (approvalStatus === 'pending_approval') {
      if (
        currentUserApprovalAction === 'approved' ||
        currentUserApprovalAction === 'rejected'
      ) {
        urlActionProcessedRef.current = true;
        lastProcessedActionRef.current = urlActionStatus;
        return;
      }
      if (!isCurrentUserApprover && currentApproval) {
        urlActionProcessedRef.current = true;
        lastProcessedActionRef.current = urlActionStatus;
        return;
      }
      if (!approvalData) return;
    }

    urlActionProcessedRef.current = true;
    lastProcessedActionRef.current = urlActionStatus;
    urlActionTriggeredRef.current = true;

    if (urlActionStatus === 'rejected' && !rejectActions.isOpen) {
      rejectActions.onOpen();
    } else if (urlActionStatus === 'approved') {
      approveJob();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    urlActionStatus,
    isStandaloneApprovalView,
    jobId,
    jobDataFromStore,
    approvalData,
    currentApproval,
    approvalStatus,
    currentUserApprovalAction,
    isCurrentUserApprover,
    location.search,
  ]);

  const isApprovalStatus =
    approvalStatus === 'pending_approval' ||
    approvalStatus === 'approved' ||
    approvalStatus === 'rejected';
  const showStandaloneFooter =
    isStandaloneApprovalView && !!jobId && isApprovalStatus;

  return {
    currentStatus,
    approvalStatus,
    approvalData,
    currentUserApprovalAction,
    isCurrentUserApprover,
    rejectActions,
    rejectApproveFields,
    handleApproveForPublishing,
    handleRejectApprove,
    approveJob,
    isApproverActionLoading,
    showStandaloneFooter,
    currentApproval,
    job_approval_enabled,
  };
};
